/**
 * Routes the post-workout token payout through the server.
 *
 * The legacy `checkFraudAndEarnTokens` (core.js:6729) decided the payout in the
 * browser: it counted sets from the in-memory payload, picked the amount, and
 * PATCHed `daily_workout_count` / `weekly_tokens_earned` /
 * `last_workout_credited_at` onto `profiles`.
 *
 * Phase 0a's guard trigger reverts those three columns. Production accumulated
 * nine `entitlement_violations` rows proving it — one per real workout — while
 * the client cheerfully reported the tokens as earned. The counters never
 * persisted, so the daily and weekly caps reset on every page load.
 *
 * `claim_workout_reward` moves the whole decision server-side and pays exactly
 * once per workout. This module is the seam that calls it.
 */

import { wrapGlobal, legacy, toast } from '../../runtime/legacy.js';

/** Copy matching what the RPC refused, so the user is told the real reason. */
const CAP_MESSAGES = {
  daily_cap_exceeded: 'Workout saved! Daily token limit reached (3/day).',
  weekly_cap_exceeded: 'Workout saved! Weekly token limit reached.',
  too_short_duration: 'Workout saved! Token credit requires a longer session.',
};

/**
 * Mirrors the server's answer back into the legacy AppState.
 *
 * The legacy UI reads `AppState.tokenBalance` and re-renders from it, so
 * without this the balance on screen would lag a page load behind the truth.
 */
function syncState(AppState, result) {
  if (typeof result.balance === 'number') {
    AppState.tokenBalance = result.balance;
    AppState.profile.token_balance = result.balance;
  }
  if (typeof result.daily_count === 'number') {
    AppState.profile.daily_workout_count = result.daily_count;
  }
  const update = legacy('updateTokenDisplay');
  if (typeof update === 'function') update();
}

/**
 * Recomputes missions the way the legacy function did on its way out, keeping
 * already-claimed state so a refresh cannot re-open a claimed mission.
 */
function refreshMissions(AppState) {
  const compute = legacy('computeUserMissions');
  if (typeof compute !== 'function') return;

  const previous = AppState.userMissions || [];
  const next = compute();
  next.forEach((mission) => {
    const before = previous.find((m) => m.id === mission.id);
    if (before && (before.claimed || before.status === 'claimed')) {
      mission.claimed = true;
      mission.status = 'claimed';
    }
  });
  AppState.userMissions = next;
}

export function installWorkoutRewards() {
  wrapGlobal('checkFraudAndEarnTokens', async function claim(next, workout, workoutId) {
    const AppState = legacy('AppState');
    const supabase = legacy('supabaseClient');

    // Demo mode has no server to ask, and the legacy path is already local-only
    // there. Anything else without an id cannot be claimed, since the RPC keys
    // the payout on the workout row.
    if (legacy('isDemoMode') || !supabase || !AppState?.user || !workoutId) {
      return next(workout, workoutId);
    }

    let result;
    try {
      const { data, error } = await supabase.rpc('claim_workout_reward', {
        p_workout_id: workoutId,
      });
      if (error) throw error;
      result = data;
    } catch (err) {
      // Never fall back to the legacy path here. Its writes are reverted by the
      // guard, so "falling back" would mean telling the user they earned tokens
      // they did not get — the exact failure this replaces.
      console.error('[ironpact] claim_workout_reward failed', err);
      toast("Workout saved. Tokens couldn't be credited right now.", 'error', 4000);
      refreshMissions(AppState);
      return undefined;
    }

    if (!result || result.ok !== true) {
      const reason = result?.error || 'unknown';
      console.error('[ironpact] claim_workout_reward refused:', reason);
      toast('Workout saved. Tokens could not be credited.', 'error', 4000);
      refreshMissions(AppState);
      return undefined;
    }

    syncState(AppState, result);

    if (result.already_claimed) {
      // Re-finishing an already-paid workout. Silent on purpose: the user did
      // nothing wrong and has already seen the reward once.
    } else if (result.capped) {
      toast(CAP_MESSAGES[result.capped] || 'Workout saved!', 'success');
    } else if (result.suspicious) {
      toast(
        `Workout saved! +${result.amount} ⬡ tokens earned (weight flagged for review).`,
        'success',
      );
    } else {
      toast(`Workout complete! +${result.amount} ⬡ tokens earned. 💪`, 'pr', 4000);
    }

    refreshMissions(AppState);
    return undefined;
  });
}
